import { useState, useEffect } from 'react';
import { settingsAPI, companiesAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import {
  Settings as SettingsIcon,
  Mail,
  Eye,
  EyeOff,
  Shield,
  Loader2,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  Target,
  User,
  Building2,
  Plus,
} from 'lucide-react';

// Toggle Switch Component
function Toggle({ enabled, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// Title options
const TITLE_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof.', label: 'Prof.' },
  { value: 'Mx.', label: 'Mx.' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export default function BuyerSettings() {
  const { refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false);
  const [orgSaveSuccess, setOrgSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Organization state
  const [company, setCompany] = useState(null);
  const [subOrgs, setSubOrgs] = useState([]);
  const [selectedSubOrgId, setSelectedSubOrgId] = useState('');
  const [newSubOrgName, setNewSubOrgName] = useState('');
  const [showNewSubOrgInput, setShowNewSubOrgInput] = useState(false);

  // Personal profile state
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    preferred_name: '',
    title: '',
  });

  // Settings state
  const [settings, setSettings] = useState({
    // Notification settings
    email_on_new_message: true,
    email_on_new_follower: true,
    email_on_new_match: true,
    email_on_meeting_request: true,
    email_digest_frequency: 'instant',
    // Privacy settings
    anonymous_browsing: false,
    show_company_in_views: true,
    // Communication
    allow_direct_messages: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load settings, profile, and company in parallel
      const [settingsRes, profileRes, companyRes] = await Promise.allSettled([
        settingsAPI.get(),
        companiesAPI.getUserProfile(),
        companiesAPI.getMyCompany(),
      ]);

      if (settingsRes.status === 'fulfilled' && settingsRes.value.data?.data) {
        setSettings(prev => ({
          ...prev,
          ...settingsRes.value.data.data,
        }));
      }

      if (profileRes.status === 'fulfilled' && profileRes.value.data?.data) {
        const profileData = profileRes.value.data.data;
        setProfile({
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          preferred_name: profileData.preferred_name || '',
          title: profileData.title || '',
        });
        // Set current sub-organization from profile
        if (profileData.sub_organization_id) {
          setSelectedSubOrgId(profileData.sub_organization_id);
        }
      }

      if (companyRes.status === 'fulfilled' && companyRes.value.data?.data) {
        const companyData = companyRes.value.data.data;
        setCompany(companyData);
        setSubOrgs(companyData.sub_organizations || []);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      // Use default settings on error
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.get();
      if (response.data?.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data.data,
        }));
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      // Use default settings on error
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleDigestChange = (value) => {
    setSettings(prev => ({
      ...prev,
      email_digest_frequency: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      await settingsAPI.update(settings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setError(null);
    setProfileSaveSuccess(false);

    try {
      await companiesAPI.updateUserProfile(profile);
      setProfileSaveSuccess(true);
      setTimeout(() => setProfileSaveSuccess(false), 3000);
      // Refresh user profile to update display name across the app
      if (refreshProfile) {
        await refreshProfile();
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('Failed to save profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCreateSubOrg = async () => {
    if (!newSubOrgName.trim() || !company?.id) return;

    setSavingOrg(true);
    setError(null);

    try {
      const res = await companiesAPI.createSubOrganization(company.id, {
        name: newSubOrgName.trim(),
      });
      const newSubOrg = res.data.data;
      setSubOrgs(prev => [...prev, newSubOrg]);
      setSelectedSubOrgId(newSubOrg.id);
      setNewSubOrgName('');
      setShowNewSubOrgInput(false);
      // Also save the sub-org to user profile
      await companiesAPI.updateUserProfile({ sub_organization_id: newSubOrg.id });
      setOrgSaveSuccess(true);
      setTimeout(() => setOrgSaveSuccess(false), 3000);
      if (refreshProfile) {
        await refreshProfile();
      }
    } catch (err) {
      console.error('Failed to create sub-organization:', err);
      setError('Failed to create sub-organization. Please try again.');
    } finally {
      setSavingOrg(false);
    }
  };

  const handleSaveSubOrg = async () => {
    setSavingOrg(true);
    setError(null);
    setOrgSaveSuccess(false);

    try {
      await companiesAPI.updateUserProfile({
        sub_organization_id: selectedSubOrgId || null,
      });
      setOrgSaveSuccess(true);
      setTimeout(() => setOrgSaveSuccess(false), 3000);
      if (refreshProfile) {
        await refreshProfile();
      }
    } catch (err) {
      console.error('Failed to save sub-organization:', err);
      setError('Failed to save sub-organization. Please try again.');
    } finally {
      setSavingOrg(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <SettingsIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-500 text-sm">Manage your notifications and privacy</p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm font-medium text-green-800">Settings saved successfully!</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm font-medium text-red-800">{error}</p>
        </div>
      )}

      {/* Profile Success Message */}
      {profileSaveSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm font-medium text-green-800">Profile saved successfully!</p>
        </div>
      )}

      {/* Personal Profile Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <User className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Personal Profile</h2>
            <p className="text-sm text-gray-500">Your name and how you'll appear to others</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              value={profile.first_name}
              onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Jane"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              value={profile.last_name}
              onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Smith"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preferred Name
              <span className="text-gray-400 font-normal ml-1">(optional)</span>
            </label>
            <input
              type="text"
              value={profile.preferred_name}
              onChange={(e) => setProfile({ ...profile, preferred_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Display name used in messages"
            />
            <p className="text-xs text-gray-500 mt-1">
              If set, this will be shown instead of your first name
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <select
              value={profile.title}
              onChange={(e) => setProfile({ ...profile, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {TITLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
          <button
            onClick={handleSaveProfile}
            disabled={savingProfile}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {savingProfile ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Save Profile
              </>
            )}
          </button>
        </div>
      </div>

      {/* Organization Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-4 h-4 text-teal-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Organization</h2>
            <p className="text-sm text-gray-500">Your company and department</p>
          </div>
        </div>

        {/* Organization Success Message */}
        {orgSaveSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-sm font-medium text-green-800">Organization updated!</p>
          </div>
        )}

        {company ? (
          <div className="space-y-4">
            {/* Company Name (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                {company.name}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Based on your email domain
              </p>
            </div>

            {/* Sub-Organization Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department / Division
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>

              {!showNewSubOrgInput ? (
                <div className="flex gap-2">
                  <select
                    value={selectedSubOrgId}
                    onChange={(e) => setSelectedSubOrgId(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">None selected</option>
                    {subOrgs.map((subOrg) => (
                      <option key={subOrg.id} value={subOrg.id}>
                        {subOrg.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowNewSubOrgInput(true)}
                    className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1"
                    title="Add new department"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm">New</span>
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSubOrgName}
                    onChange={(e) => setNewSubOrgName(e.target.value)}
                    placeholder="e.g., Oncology, Vaccines, R&D"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleCreateSubOrg}
                    disabled={!newSubOrgName.trim() || savingOrg}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    {savingOrg ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    <span className="text-sm">Add</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewSubOrgInput(false);
                      setNewSubOrgName('');
                    }}
                    className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Select or create your department to help colleagues find you
              </p>
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-100">
              <button
                onClick={handleSaveSubOrg}
                disabled={savingOrg}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {savingOrg ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Save Organization
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No company associated with your account yet.</p>
            <p className="text-xs text-gray-400 mt-1">Your company will be detected from your email domain.</p>
          </div>
        )}
      </div>

      {/* Email Notifications */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <Mail className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Email Notifications</h2>
            <p className="text-sm text-gray-500">Choose when to receive email alerts</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* New Message */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900">New Messages</p>
                <p className="text-sm text-gray-500">Receive an email when a seller sends you a message</p>
              </div>
            </div>
            <Toggle
              enabled={settings.email_on_new_message}
              onChange={() => handleToggle('email_on_new_message')}
            />
          </div>

          {/* New Follower - hidden for buyers, they don't get followers */}

          {/* New Match */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900">New Matches</p>
                <p className="text-sm text-gray-500">Get notified when new sellers match your criteria</p>
              </div>
            </div>
            <Toggle
              enabled={settings.email_on_new_match}
              onChange={() => handleToggle('email_on_new_match')}
            />
          </div>
        </div>

        {/* Email Frequency */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="font-medium text-gray-900 mb-3">Email Frequency</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { value: 'instant', label: 'Instant' },
              { value: 'daily', label: 'Daily Digest' },
              { value: 'weekly', label: 'Weekly Digest' },
              { value: 'never', label: 'Never' },
            ].map(option => (
              <button
                key={option.value}
                onClick={() => handleDigestChange(option.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  settings.email_digest_frequency === option.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Privacy</h2>
            <p className="text-sm text-gray-500">Control your visibility on the platform</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Anonymous Browsing */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <EyeOff className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900">Anonymous Browsing</p>
                <p className="text-sm text-gray-500">
                  Browse seller profiles without showing your company name
                </p>
              </div>
            </div>
            <Toggle
              enabled={settings.anonymous_browsing}
              onChange={() => handleToggle('anonymous_browsing')}
            />
          </div>

          {/* Show Company in Views */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900">Show Company in Activity</p>
                <p className="text-sm text-gray-500">
                  Allow sellers to see your company name when you view their profiles
                </p>
              </div>
            </div>
            <Toggle
              enabled={settings.show_company_in_views}
              onChange={() => handleToggle('show_company_in_views')}
            />
          </div>
        </div>

        {/* Info box */}
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> Anonymous browsing hides your company from profile view tracking.
            Sellers will see "Anonymous Viewer" instead of your company name.
          </p>
        </div>
      </div>

      {/* Communication Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Communication</h2>
            <p className="text-sm text-gray-500">Manage how others can contact you</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Allow Direct Messages */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900">Allow Direct Messages</p>
                <p className="text-sm text-gray-500">
                  Let sellers send you direct messages on the platform
                </p>
              </div>
            </div>
            <Toggle
              enabled={settings.allow_direct_messages}
              onChange={() => handleToggle('allow_direct_messages')}
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
}
